import type { EmailEditorProps } from "react-email-editor";
import { DesignerTheme } from "../../../shared/utility/designer-theme";

/**
 * What the Email Builder hands Unlayer when it creates an editor, and how that editor's own chrome
 * follows the designer's light/dark theme afterwards.
 *
 * `react-email-editor` destroys and re-creates the editor — dropping the design being edited —
 * whenever these options serialise differently from the previous render's. So nothing in them may
 * change while an editor is open: the theme goes in once, when the editor is created, and a later
 * flip reaches the running editor through {@link applyUnlayerTheme} instead.
 *
 * The theme is the builder's chrome only. The email being designed keeps its own colours.
 */

export type TEmailEditorOptions = NonNullable<EmailEditorProps["options"]>;

/** Unlayer's two modern builder themes. */
export type TUnlayerTheme = "modern_light" | "modern_dark";

/**
 * The one method of the running editor used here. Optional because the editor is Unlayer's
 * always-latest embed script (`editor.unlayer.com/embed.js`), not the version the typings pin.
 */
export interface IUnlayerThemeTarget {
  setTheme?: (theme: TUnlayerTheme) => void;
}

/** Kuika's accent over Unlayer's own blue on the editing affordances, injected into its iframe. */
const UNLAYER_CUSTOM_CSS: string[] = [
  `
    .blockbuilder-layer-controls .blockbuilder-layer-control, .blockbuilder-layer-drag, .blockbuilder-layer.blockbuilder-layer-selected .blockbuilder-layer-add-row, .emfPRz {
      background-color: #246eff;
    }
    .emfPRz:hover {
      background-color: #246eff;
    }
    .blockbuilder-layer {
      --accent-color: #246eff;
    }
    .blockbuilder-layer.blockbuilder-layer-selected {
      --accent-color: #246eff;
    }
    .blockbuilder-placeholder {
      background-color: #7db7ff;
    }
    .blockbuilder-placeholder .blockbuilder-placeholder-empty {
      color: #246eff;
    }
    /* An edge on Unlayer's own footer bar. Its dark theme paints that bar and the empty area of
       the tools panel above it the same near-black, so the icon rail simply stops in a void; the
       light theme's own grey already reads as a bar and takes the same line quietly. Matched on
       the class substring because the embed is always-latest and hashes its class names, and set
       over Unlayer's own border, which is why it is !important. Drawing it here rather than over
       the iframe keeps it on the bar itself: it narrows, and disappears, with the panel. */
    [class*="branding" i] {
      border-top: 1px solid rgba(128, 128, 128, 0.4) !important;
    }
  `
];

export const toUnlayerTheme = (theme: DesignerTheme): TUnlayerTheme => {
  return theme === "dark" ? "modern_dark" : "modern_light";
};

/** The options an editor is created with, in the Unlayer theme matching the designer's `theme`. */
export const buildEmailEditorOptions = (theme: DesignerTheme): TEmailEditorOptions => {
  return {
    appearance: { theme: toUnlayerTheme(theme) },
    customCSS: UNLAYER_CUSTOM_CSS
  };
};

/**
 * Repaints a running editor's chrome in `theme` without re-creating it, so the design in it is
 * untouched. Returns false — and does nothing — where there is no editor yet, or where the loaded
 * embed has no `setTheme`; that editor then keeps the theme it was created with.
 */
export const applyUnlayerTheme = (editor: IUnlayerThemeTarget | null | undefined, theme: TUnlayerTheme): boolean => {
  if (typeof editor?.setTheme !== "function") {
    return false;
  }

  editor.setTheme(theme);
  return true;
};
